import React from "react";
import { toast } from "sonner";
import { AlertTriangle, Archive, Loader2, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const RECOVERY_DAYS = 30;

/**
 * Phase 10 (§28, GRP-DEC-212…222) — the Owner's lifecycle controls.
 *
 * Archive is offered FIRST and framed as the reversible default (GRP-DEC-217); deletion is a
 * multi-step, consequence-first flow with a typed group-name confirmation and an explicit
 * explanation of the 30-day cancellation window. Owner-only: Admins never see this card.
 */
export function GroupDangerZone({ group, viewer, onChanged }) {
  const [mode, setMode] = React.useState(null);        // archive | delete | draft
  const [step, setStep] = React.useState(1);
  const [typed, setTyped] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  if (!viewer?.is_owner) return null;

  const isDraft = group?.state === "draft";
  const name = group?.display_name || "";
  const close = () => { setMode(null); setStep(1); setTyped(""); };
  const nameOk = typed.trim().toLowerCase() === name.trim().toLowerCase() && !!name.trim();

  const run = async (fn, message) => {
    setBusy(true);
    try {
      await fn();
      toast.success(message);
      close();
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "That didn't work. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border border-destructive/40 space-y-4 p-5" data-testid="group-danger-zone">
      <div>
        <h2 className="text-base font-[640] text-destructive">Danger zone</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Only the owner can archive or delete this group.
        </p>
      </div>

      {isDraft ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
          <div>
            <p className="text-sm font-medium">Delete this draft</p>
            <p className="text-xs text-muted-foreground">
              Drafts were never published, so this is immediate and can&apos;t be undone.
            </p>
          </div>
          <Button variant="destructive" size="sm" data-testid="group-draft-delete-open"
                  onClick={() => { setMode("draft"); setStep(1); }}>
            <Trash2 className="mr-2 h-4 w-4" />Delete draft
          </Button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
            <div>
              <p className="text-sm font-medium">Archive this group</p>
              <p className="text-xs text-muted-foreground">
                Recommended. The group leaves discovery and becomes unavailable, nothing is
                deleted, and you can restore it any time from My groups.
              </p>
            </div>
            <Button variant="outline" size="sm" data-testid="group-archive-open"
                    onClick={() => { setMode("archive"); setStep(1); }}>
              <Archive className="mr-2 h-4 w-4" />Archive
            </Button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/40 p-3">
            <div>
              <p className="text-sm font-medium text-destructive">Delete this group</p>
              <p className="text-xs text-muted-foreground">
                Starts a {RECOVERY_DAYS}-day cancellation window. After that everything is
                permanently removed.
              </p>
            </div>
            <Button variant="destructive" size="sm" data-testid="group-delete-open"
                    onClick={() => { setMode("delete"); setStep(1); setTyped(""); }}>
              <Trash2 className="mr-2 h-4 w-4" />Delete group
            </Button>
          </div>
        </>
      )}

      {/* ── Archive ───────────────────────────────────────────────────────── */}
      <Dialog open={mode === "archive"} onOpenChange={(v) => (v ? null : close())}>
        <DialogContent data-testid="group-archive-dialog">
          <DialogHeader>
            <DialogTitle>Archive {name}?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>While archived:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>The group disappears from discovery and search, and its page shows an archived notice.</li>
                  <li>Streams, posts, members and private conversations become unavailable.</li>
                  <li>Posts shared to global Chatter from this group are withdrawn.</li>
                  <li>Publicly listed events and resources stay available on their own pages.</li>
                  <li>Nothing is deleted, and your @handle stays reserved.</li>
                </ul>
                <p>You can restore it any time from My groups.</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={close} data-testid="group-archive-cancel">Cancel</Button>
            <Button disabled={busy} data-testid="group-archive-confirm"
                    onClick={() => run(() => api.post(`/groups/${group.id}/archive`),
                                       "Group archived.")}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Archive group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete (two steps + typed name) ──────────────────────────────── */}
      <Dialog open={mode === "delete"} onOpenChange={(v) => (v ? null : close())}>
        <DialogContent data-testid="group-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              {step === 1 ? `Delete ${name}?` : "Confirm permanent deletion"}
            </DialogTitle>
            <DialogDescription asChild>
              {step === 1 ? (
                <div className="space-y-2 text-sm">
                  <p>Deleting this group will permanently remove, after {RECOVERY_DAYS} days:</p>
                  <ul className="list-disc space-y-1 pl-5">
                    <li>{group?.member_count || 0} membership{(group?.member_count || 0) === 1 ? "" : "s"}, every stream and every post and comment inside the group.</li>
                    <li>Private conversations between members of this group.</li>
                    <li>Invitations, invite links, membership questions and answers.</li>
                    <li>The group&apos;s @handle, which is released for anyone to claim.</li>
                  </ul>
                  <p className="rounded-md bg-muted/60 p-3">
                    Archiving does all the same hiding and keeps everything. If you only want
                    the group to go quiet, archive it instead.
                  </p>
                </div>
              ) : (
                <div className="space-y-2 text-sm">
                  <p>
                    The group becomes unavailable immediately and is scheduled for permanent
                    deletion in {RECOVERY_DAYS} days. You can cancel from My groups at any
                    point during that window — after it closes, nothing can be restored.
                  </p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          {step === 2 ? (
            <div className="space-y-1.5">
              <Label>
                Type <span className="font-mono font-semibold">{name}</span> to confirm
              </Label>
              <Input value={typed} onChange={(e) => setTyped(e.target.value)}
                     placeholder={name} data-testid="group-delete-typed-name" />
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={close} data-testid="group-delete-cancel">Cancel</Button>
            {step === 1 ? (
              <>
                <Button variant="outline" data-testid="group-delete-archive-instead"
                        onClick={() => { setMode("archive"); setStep(1); }}>
                  Archive instead
                </Button>
                <Button variant="destructive" data-testid="group-delete-continue"
                        onClick={() => setStep(2)}>
                  Continue
                </Button>
              </>
            ) : (
              <Button variant="destructive" disabled={!nameOk || busy}
                      data-testid="group-delete-confirm"
                      onClick={() => run(
                        () => api.post(`/groups/${group.id}/request-deletion`,
                                       { confirm_name: typed }),
                        `Deletion scheduled. You have ${RECOVERY_DAYS} days to cancel.`)}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Schedule deletion
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Draft hard delete ────────────────────────────────────────────── */}
      <Dialog open={mode === "draft"} onOpenChange={(v) => (v ? null : close())}>
        <DialogContent data-testid="group-draft-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete this draft?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  This draft was never published, so it is removed immediately and cannot be
                  recovered. If anything already depends on it, we&apos;ll tell you and you can
                  publish it and use the standard deletion process instead.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>
              Type <span className="font-mono font-semibold">{name || "the group name"}</span> to confirm
            </Label>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)}
                   placeholder={name} data-testid="group-draft-delete-typed-name" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={close} data-testid="group-draft-delete-cancel">Cancel</Button>
            <Button variant="destructive" disabled={!nameOk || busy}
                    data-testid="group-draft-delete-confirm"
                    onClick={() => run(
                      () => api.delete(`/groups/${group.id}/draft`,
                                       { data: { confirm_name: typed } }),
                      "Draft deleted.")}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Delete draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
